import { format, parse } from "date-fns";
import huLocale from "date-fns/locale/hu";

/**
 * Format date string to Hungarian locale date format (yyyy. MM. dd.)
 *
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted date string in "yyyy. MM. dd." format or original string if invalid
 *
 * @example
 * formatDateHu('2026-09-01') // "2026. 09. 01."
 * formatDateHu('invalid') // "invalid"
 */
export const formatDateHu = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return dateString;
  }
};

/**
 * Format date string to Hungarian locale period format (year. month)
 *
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted period string in "yyyy. month" format (e.g., "2026. szeptember")
 *
 * @example
 * formatPeriodHu('2026-09-01') // "2026. szeptember"
 * formatPeriodHu('invalid') // Falls back to formatDateHu
 */
export const formatPeriodHu = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleDateString("hu-HU", { month: "long" });
    return `${year}. ${month}`;
  } catch {
    return formatDateHu(dateString);
  }
};

/**
 * Format date string to abbreviated Hungarian month format with capitalized first letter
 *
 * @param rawDateStr - Date string in "yyyy.MM.dd" format
 * @returns Formatted date string in "MMM dd." format with capitalized month (e.g., "Szept. 01.")
 *
 * @example
 * formatShortDateHu('2026.09.01') // "Szept. 01."
 * formatShortDateHu('invalid') // "invalid"
 */
export const formatShortDateHu = (rawDateStr: string): string => {
  try {
    const date = parse(rawDateStr, "yyyy.MM.dd", new Date());
    const formatted = format(date, "MMM dd.", { locale: huLocale });
    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return rawDateStr;
  }
};
